/**
 * NPI Tabs Frontend JavaScript
 * Based on WAI ARIA Tabs Manual pattern
 * Responsive: Tabs on desktop, Accordion on mobile
 */

class NPITabs {
	constructor(tabsContainer) {
		this.tabsContainer = tabsContainer;
		
		// Check if already initialized
		if (this.tabsContainer.dataset.tabsInitialized) {
			return;
		}
		this.tabsContainer.dataset.tabsInitialized = 'true';
		
		// Get tab panels and create tabs from them
		this.tabPanels = Array.from(tabsContainer.querySelectorAll('.wp-block-npi-tab-panel'));
		
		// Exit if no panels found
		if (this.tabPanels.length === 0) {
			return;
		}
		
		this.setupTabsFromPanels();
		
		// Set initial active tab
		this.activeTabIndex = 0;
		
		// Bind methods
		this.handleTabClick = this.handleTabClick.bind(this);
		this.handleTabKeydown = this.handleTabKeydown.bind(this);
		this.handleAccordionToggle = this.handleAccordionToggle.bind(this);
		this.handleResize = this.handleResize.bind(this);
		
		this.init();
	}
	
	setupTabsFromPanels() {
		// Check if tabs header already exists
		if (this.tabsContainer.querySelector('.tabs-header')) {
			this.tabList = this.tabsContainer.querySelector('.tab-list');
			this.tabs = Array.from(this.tabList.querySelectorAll('[role="tab"]'));
			return;
		}
		
		// Create tab list container
		const tabsHeader = document.createElement('div');
		tabsHeader.className = 'tabs-header';
		
		const tabList = document.createElement('div');
		tabList.setAttribute('role', 'tablist');
		tabList.className = 'tab-list';
		tabList.setAttribute('aria-label', 'Content tabs');
		
		// Create tabs from panels
		this.tabs = [];
		this.tabPanels.forEach((panel, index) => {
			const label = panel.dataset.tabLabel || `Tab ${index + 1}`;
			const tabId = `tab-${index}-${Math.random().toString(36).substr(2, 5)}`;
			const panelId = `panel-${index}-${Math.random().toString(36).substr(2, 5)}`;
			
			// Create tab button
			const tabButton = document.createElement('button');
			tabButton.setAttribute('role', 'tab');
			tabButton.className = 'tab-button';
			tabButton.setAttribute('id', tabId);
			tabButton.setAttribute('aria-controls', panelId);
			tabButton.setAttribute('aria-selected', index === 0);
			tabButton.setAttribute('tabindex', index === 0 ? '0' : '-1');
			tabButton.textContent = label;
			
			// Set up panel
			panel.setAttribute('role', 'tabpanel');
			panel.setAttribute('id', panelId);
			panel.setAttribute('aria-labelledby', tabId);
			panel.setAttribute('tabindex', '0');
			panel.classList.add('tab-panel');
			if (index === 0) {
				panel.classList.add('active');
				tabButton.classList.add('active');
			}
			
			this.tabs.push(tabButton);
			tabList.appendChild(tabButton);
		});
		
		tabsHeader.appendChild(tabList);
		this.tabsContainer.insertBefore(tabsHeader, this.tabsContainer.firstChild);
		this.tabList = tabList;
	}
	
	init() {
		// Set up initial state
		this.setActiveTab(this.activeTabIndex);
		
		// Add event listeners for tabs
		this.tabs.forEach((tab, index) => {
			tab.addEventListener('click', () => this.handleTabClick(index));
			tab.addEventListener('keydown', this.handleTabKeydown);
		});
		
		// Set up responsive behavior
		this.handleResize();
		window.addEventListener('resize', this.handleResize);
		
		// Initialize accordion functionality for mobile
		this.setupAccordion();
	}
	
	handleTabClick(index) {
		this.setActiveTab(index);
	}
	
	handleTabKeydown(event) {
		const currentIndex = this.tabs.indexOf(event.target);
		let targetIndex = currentIndex;
		
		switch (event.key) {
			case 'ArrowLeft':
				targetIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
				break;
			case 'ArrowRight':
				targetIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
				break;
			case 'Home':
				targetIndex = 0;
				break;
			case 'End':
				targetIndex = this.tabs.length - 1;
				break;
			case 'Enter':
			case ' ':
				event.preventDefault();
				this.setActiveTab(currentIndex);
				return;
			default:
				return; // Exit early for other keys
		}
		
		event.preventDefault();
		this.focusTab(targetIndex);
	}
	
	setActiveTab(index) {
		// Allow -1 for mobile accordion (all closed), but validate other indices
		if (index >= this.tabs.length) return;
		if (index < -1) return;
		
		this.activeTabIndex = index;
		
		// Update tabs
		this.tabs.forEach((tab, i) => {
			const isActive = i === index;
			tab.setAttribute('aria-selected', isActive);
			tab.setAttribute('tabindex', isActive ? '0' : '-1');
			tab.classList.toggle('active', isActive);
		});
		
		// Update panels
		this.tabPanels.forEach((panel, i) => {
			const isActive = i === index;
			panel.classList.toggle('active', isActive);
			panel.hidden = !isActive;
		});
		
		// Update accordion headers
		this.updateAccordionState(index);
	}
	
	focusTab(index) {
		if (this.tabs[index]) {
			this.tabs[index].focus();
		}
	}
	
	setupAccordion() {
		// Create accordion headers for mobile view (only if they don't exist)
		this.tabPanels.forEach((panel, index) => {
			// Check if accordion header already exists
			const existingHeader = panel.previousElementSibling;
			if (existingHeader && existingHeader.classList.contains('accordion-header')) {
				return;
			}
			
			const accordionHeader = document.createElement('button');
			accordionHeader.className = 'accordion-header';
			accordionHeader.setAttribute('aria-expanded', index === this.activeTabIndex);
			accordionHeader.setAttribute('aria-controls', panel.id);
			accordionHeader.textContent = this.tabs[index].textContent;
			accordionHeader.addEventListener('click', () => this.handleAccordionToggle(index));
			
			// Insert accordion header before the panel
			panel.parentNode.insertBefore(accordionHeader, panel);
		});
	}
	
	handleAccordionToggle(index) {
		// Only handle accordion behavior on mobile
		if (!this.isMobile()) return;
		
		const isCurrentlyActive = this.activeTabIndex === index;
		
		if (isCurrentlyActive) {
			// Close current panel
			this.setActiveTab(-1); // No active tab
		} else {
			// Open clicked panel
			this.setActiveTab(index);
		}
	}
	
	updateAccordionState(activeIndex) {
		const accordionHeaders = this.tabsContainer.querySelectorAll('.accordion-header');
		accordionHeaders.forEach((header, index) => {
			const isActive = index === activeIndex;
			header.setAttribute('aria-expanded', isActive);
			header.classList.toggle('active', isActive);
		});
	}
	
	handleResize() {
		// Update layout based on screen size
		this.tabsContainer.classList.toggle('mobile-view', this.isMobile());
		
		// Ensure proper state on resize
		if (!this.isMobile() && this.activeTabIndex === -1) {
			// If no tab is active on desktop, activate first tab
			this.setActiveTab(0);
		}
	}
	
	isMobile() {
		return window.matchMedia('(max-width: 767px)').matches;
	}
	
	// Public method to destroy instance
	destroy() {
		window.removeEventListener('resize', this.handleResize);
		// Remove accordion headers
		const accordionHeaders = this.tabsContainer.querySelectorAll('.accordion-header');
		accordionHeaders.forEach(header => header.remove());
	}
}

// Initialize tabs when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
	const tabsContainers = document.querySelectorAll('.wp-block-npi-tabs');
	tabsContainers.forEach(container => {
		// Skip if already initialized
		if (!container.dataset.tabsInitialized) {
			new NPITabs(container);
		}
	});
});

// Also handle dynamic content loading (like block editor preview)
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initializeTabs);
} else {
	initializeTabs();
}

function initializeTabs() {
	const tabsContainers = document.querySelectorAll('.wp-block-npi-tabs:not([data-tabs-initialized])');
	tabsContainers.forEach(container => {
		new NPITabs(container);
	});
}